/**
 * Development Logging Utility
 *
 * Provides console logging only in development mode
 * with enhanced API error handling
 */

const isDevelopment = process.env.NODE_ENV === 'development';

export const logger = {
  log: (...args) => {
    if (isDevelopment) {
      console.log(...args);
    }
  },

  error: (...args) => {
    if (isDevelopment) {
      console.error(...args);
    }
  },

  warn: (...args) => {
    if (isDevelopment) {
      console.warn(...args);
    }
  },

  info: (...args) => {
    if (isDevelopment) {
      console.info(...args);
    }
  },

  /**
   * Log a successful operation
   */
  success: (...args) => {
    if (isDevelopment) {
      console.log('✅', ...args);
    }
  },

  /**
   * Handle API errors with appropriate logging level
   * @param {string} operation - The operation that failed
   * @param {Error} error - The error object
   * @param {boolean} isExpected - Whether this error is expected/handled
   */
  handleApiError: (operation, error, isExpected = false) => {
    if (!isDevelopment) return;

    if (isExpected || error.response?.status === 404) {
      console.log(`ℹ️  ${operation}: Not found (expected for new resources)`);
    } else if (error.code === 'ERR_NETWORK' || error.code === 'ECONNREFUSED') {
      console.warn(`⚠️  ${operation}: Network connection unavailable`);
    } else {
      console.error(`❌  ${operation}: Unexpected error`, {
        status: error.response?.status,
        message: error.message,
        code: error.code
      });
    }
  },

  /**
   * Log API operations with context
   */
  apiSuccess: (operation, data = null) => {
    if (isDevelopment) {
      if (data) {
        console.log(`✅  ${operation}`, data);
      } else {
        console.log(`✅  ${operation}`);
      }
    }
  },

  /**
   * Performance measurement utilities
   */
  time: (label) => {
    if (isDevelopment) {
      console.time(label);
    }
  },

  timeEnd: (label) => {
    if (isDevelopment) {
      console.timeEnd(label);
    }
  },

  /**
   * Group related logs (development only)
   */
  group: (label) => {
    if (isDevelopment) {
      console.group(label);
    }
  },

  groupEnd: () => {
    if (isDevelopment) {
      console.groupEnd();
    }
  }
};
